import request from '@/request/index'

/**
 * 获取OSS上传策略和临时凭证
 * @param {string} bucket - 存储空间名称，默认为 'good-yake'
 * @returns {Promise} 返回包含上传所需凭证的Promise
 * @property {string} accessKeyId - 临时访问密钥ID
 * @property {string} policy - 上传策略
 * @property {string} signature - 签名信息
 * @property {string} dir - 上传目录
 * @property {string} host - 上传域名
 * @property {string} callback - 上传回调
 * @property {string} expire - 过期时间
 */
export function getOssPolicy(bucket = 'yacoo-official-upload') {
  return request.get('/oss/policy', {
    bucket
  }, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * 生成当前日期字符串 YYYYMMDD
 * @returns {string}
 */
function getNowDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

/**
 * 生成UUID
 * @returns {string}
 */
function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * OSS文件直传
 * @param {File} file - 要上传的文件对象
 * @param {string} [customPath] - 自定义文件路径，可选
 * @returns {Promise<string>} 返回文件的访问URL
 */
export async function uploadToOss(file, customPath) {
  try {
    // 1. 获取OSS策略和临时凭证
    const policyResult = await getOssPolicy()
    if (!policyResult.data) {
      throw new Error('Failed to get OSS policy')
    }
    
    const policy = policyResult.data
    const formData = new FormData()
    
    // 2. 构建文件路径
    const ext = '.' + fileUtils.getExtension(file.name)
    const key = policy.dir + '/' + getNowDate() + uuid() + ext
    
    // 3. 组装上传表单数据
    formData.append('OSSAccessKeyId', policy.accessKeyId)
    formData.append('policy', policy.policy)
    formData.append('signature', policy.signature)
    formData.append('key', key)
    formData.append('success_action_status', '200')
    
    if (policy.callback) {
      formData.append('callback', policy.callback)
    }
    
    // 文件必须最后添加
    formData.append('file', file)

    // 4. 执行上传请求
    const response = await fetch(policy.host, {
      method: 'POST',
      body: formData
    })

    // 5. 检查响应状态
    if (!response.ok && response.status !== 200) {
      const errorText = await response.text()
      throw new Error(`Upload failed: ${errorText}`)
    }

    // 6. 返回完整的文件访问URL
    return `${policy.host}/${key}`
  } catch (error) {
    console.error('OSS upload error:', error)
    throw new Error(`文件上传失败: ${error.message}`)
  }
}

/**
 * 便捷上传方法，支持批量上传
 * @param {File|File[]} files - 要上传的文件对象或文件对象数组
 * @param {string} [customPath] - 自定义文件路径，可选
 * @returns {Promise<string|string[]>} 返回文件的访问URL或URL数组
 */
export async function upload(files, customPath) {
  try {
    // 判断是否为数组
    if (Array.isArray(files)) {
      // 批量上传
      const uploadPromises = files.map(file => 
        uploadToOss(file, customPath ? `${customPath}/${file.name}` : null)
      )
      return await Promise.all(uploadPromises)
    } else {
      // 单文件上传
      return await uploadToOss(files, customPath)
    }
  } catch (error) {
    console.error('Upload process failed:', error)
    throw new Error(`上传过程失败: ${error.message}`)
  }
}

/**
 * 检查文件类型是否允许上传
 * @param {File} file - 要检查的文件
 * @param {string[]} allowedTypes - 允许的文件类型数组，例如 ['image/jpeg', 'image/png']
 * @returns {boolean} 是否允许上传
 */
export function checkFileType(file, allowedTypes) {
  return allowedTypes.includes(file.type)
}

/**
 * 检查文件大小是否在限制范围内
 * @param {File} file - 要检查的文件
 * @param {number} maxSize - 最大文件大小（字节）
 * @returns {boolean} 是否在限制范围内
 */
export function checkFileSize(file, maxSize) {
  return file.size <= maxSize
}

// 导出文件相关的工具函数
export const fileUtils = {
  // 获取文件扩展名
  getExtension: (filename) => {
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
  },
  
  // 格式化文件大小
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  },
  
  // 生成唯一文件名
  generateUniqueFileName: (originalName) => {
    const ext = originalName.slice((originalName.lastIndexOf('.') - 1 >>> 0) + 2)
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`
  }
} 